import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createRoomCategory,
  getAllRoomCategories,
  getRoomCategoryByID,
  updateRoomCategory,
} from "./services/roomCategory.services";
import { clearRoomCategoryDetails } from "./roomCategorySlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { closed } from "../../components/drawer/drawerSlice";

const CreateRoomCategory: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const RoomCategoryDetail = useAppSelector(
    (state) => state.roomCategory.RoomCategoryDetail
  );
  const RoomCategoryID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.roomCategory.formLoading);

  useEffect(() => {
    if (RoomCategoryID) {
      dispatch(getRoomCategoryByID(RoomCategoryID));
    }
  }, [RoomCategoryID]);

  useEffect(() => {
    return () => {
      dispatch(clearRoomCategoryDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!RoomCategoryID) {
        const insertPayload = {
          RoomCategory: values.RoomCategory ? values.RoomCategory : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createRoomCategory(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearRoomCategoryDetails());
          dispatch(getAllRoomCategories());
        }
      } else {
        const editPayload = {
          ID: RoomCategoryID,
          RoomCategory: values.RoomCategory ? values.RoomCategory : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateRoomCategory(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearRoomCategoryDetails());
          dispatch(getAllRoomCategories());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={RoomCategoryDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="RoomCategory"
                label="Room Category"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name={`IsActive`}
                label="Status"
                component={FormSelectionField}
                validator={requiredValidator}
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status?.value,
                    label: status?.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginRight: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
              <ButtonWithLoading
                label={!RoomCategoryID ? "Create" : "Update"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateRoomCategory;
